import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Heading from "@tiptap/extension-heading";
import LinkExt from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import ImageExt from "@tiptap/extension-image";
import { mergeAttributes } from "@tiptap/core";
import { useEffect, useCallback, useRef, useState } from "react";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading1, Heading2, Heading3, Quote, Link, Minus, Undo, Redo,
  AlignLeft, AlignCenter, ImagePlus, Loader2,
} from "lucide-react";
import { uploadFile } from "./ImageUpload";

const SIZE_PRESETS = [
  { label: "S", title: "Small (30%)",  value: "30%" },
  { label: "M", title: "Medium (50%)", value: "50%" },
  { label: "L", title: "Large (75%)",  value: "75%" },
  { label: "↔", title: "Full width",   value: "100%" },
];

function ResizableImageView({ node, updateAttributes, selected }: NodeViewProps) {
  const { src, alt, width } = node.attrs as { src: string; alt?: string; width?: string };
  const [showBar, setShowBar] = useState(false);

  useEffect(() => {
    setShowBar(selected as boolean);
  }, [selected]);

  return (
    <NodeViewWrapper className="relative inline-block" style={{ width: width ?? "100%", maxWidth: "100%" }}>
      <img
        src={src as string}
        alt={(alt as string) ?? ""}
        style={{ width: "100%", height: "auto", display: "block", borderRadius: 8,
          outline: showBar ? "2px solid #1e3a5f" : "none",
          outlineOffset: 2,
        }}
        draggable={false}
      />
      {showBar && (
        <div
          contentEditable={false}
          className="absolute -top-8 left-0 flex items-center gap-0.5 bg-slate-900 rounded-lg px-1.5 py-1 shadow-lg z-20"
          onMouseDown={e => e.preventDefault()}
        >
          <span className="text-xs text-slate-400 pr-1 select-none">Size:</span>
          {SIZE_PRESETS.map(p => (
            <button
              key={p.value}
              type="button"
              title={p.title}
              onClick={() => updateAttributes({ width: p.value })}
              className={`px-2 py-0.5 rounded text-xs font-bold transition-colors select-none
                ${width === p.value ? "bg-primary text-white" : "text-white hover:bg-slate-700"}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </NodeViewWrapper>
  );
}

const ResizableImage = ImageExt.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: "100%", parseHTML: el => (el as HTMLElement).style.width || (el as HTMLElement).getAttribute("width") || "100%",
        renderHTML: attrs => ({ style: `width: ${attrs.width as string}` }) },
    };
  },
  renderHTML({ HTMLAttributes }) {
    return ["img", mergeAttributes(this.options.HTMLAttributes as Record<string, unknown>, HTMLAttributes)];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
}

export function RichTextEditor({ value, onChange, placeholder = "Write your article content here…", className = "" }: RichTextEditorProps) {
  const [uploading, setUploading] = useState(false);
  const imgInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: false }),
      Heading.configure({ levels: [1, 2, 3] }),
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      LinkExt.configure({ openOnClick: false, HTMLAttributes: { rel: "noopener noreferrer" } }),
      ResizableImage.configure({ inline: false }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: { class: "focus:outline-none" },
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Enter URL", prev ?? "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const handleImageFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editor) return;
    if (e.target) e.target.value = "";
    setUploading(true);
    try {
      const url = await uploadFile(file);
      const absUrl = url.startsWith("http") ? url : `${window.location.origin}${url}`;
      editor.chain().focus().setImage({ src: absUrl }).run();
    } catch {
      alert("Image upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }, [editor]);

  if (!editor) return null;

  const ToolbarBtn = ({ onClick, active = false, title, children, disabled = false }: {
    onClick: () => void; active?: boolean; title: string; children: React.ReactNode; disabled?: boolean
  }) => (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      title={title}
      disabled={disabled}
      className={`p-1.5 rounded transition-colors disabled:opacity-40 ${active ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"}`}
    >
      {children}
    </button>
  );

  const Divider = () => <div className="w-px h-5 bg-slate-200 mx-0.5" />;

  return (
    <div className={`border border-slate-200 rounded-xl overflow-hidden ${className}`}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="Heading 1"><Heading1 size={15} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Heading 2"><Heading2 size={15} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Heading 3"><Heading3 size={15} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold"><Bold size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic"><Italic size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive("underline")} title="Underline"><UnderlineIcon size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={setLink} active={editor.isActive("link")} title="Link"><Link size={14} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Bullet list"><List size={15} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Numbered list"><ListOrdered size={15} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Blockquote"><Quote size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider line" active={false}><Minus size={14} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("left").run()} active={editor.isActive({ textAlign: "left" })} title="Align left"><AlignLeft size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setTextAlign("center").run()} active={editor.isActive({ textAlign: "center" })} title="Align center"><AlignCenter size={14} /></ToolbarBtn>
        <Divider />
        <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => void handleImageFile(e)} />
        <ToolbarBtn onClick={() => imgInputRef.current?.click()} title="Insert image" active={false} disabled={uploading}>
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} title="Undo" active={false}><Undo size={14} /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} title="Redo" active={false}><Redo size={14} /></ToolbarBtn>
        <div className="ml-auto text-xs text-slate-400 px-1 hidden sm:block">
          {editor.storage.characterCount?.characters?.() ?? ""} chars
        </div>
      </div>

      {uploading && (
        <div className="px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 text-xs text-blue-600 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" />
          Uploading image…
        </div>
      )}

      <EditorContent editor={editor} />
    </div>
  );
}
